import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import Layout from "./components/Layout";
import Spinner from "react-bootstrap/Spinner";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { TicketStatusProvider } from "./context/TicketStatusContext";
import SettingsLayout from "./components/SettingsLayout";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

const DashboardPage = lazy(() => import("./pages/Dashboard/DashboardPage/DashboardPage"));
const QuotesPage = lazy(() => import("./pages/Quotes/QuotesPage"));
const QuoteFormPage = lazy(() => import("./pages/Quotes/QuoteFormPage"));

// Customer pages
const CustomerListPage = lazy(() =>
  import("./pages/Customer/CustomerListPage")
);
const AddCustomerPage = lazy(() => import("./pages/Customer/AddCustomerPage"));
const ImportCustomerPage = lazy(() => import("./pages/Customer/ImportCustomerPage"));
const EditCustomerPage = lazy(() =>
  import("./pages/Customer/EditCustomerPage")
);
const CustomerDetailPage = lazy(() =>
  import("./pages/Customer/CustomerDetailPage")
);

// CRM pages
const OpportunitiesPage = lazy(() =>
  import("./pages/Opportunities/OpportunitiesPage")
);
const OpportunityDetailPageV0 = lazy(() =>
  import("./pages/Opportunities/OpportunityDetailPage")
);
const OpportunityDetailPage = lazy(() =>
  import("./pages/Opportunities/OpportunityDetailPage/OpportunityDetailPage")
);
const ImportOpportunityPage = lazy(() =>
  import("./pages/Opportunities/ImportOpportunityPage")
);

// Task pages
const TasksPage = lazy(() => import("./pages/Task/TasksPage"));
const AssetsPage = lazy(() => import("./pages/Asset/AssetsPage"));
const ImportAssetPage = lazy(() => import("./pages/Asset/ImportAssetPage"));
const AssetDetailPage = lazy(() => import("./pages/Asset/AssetDetailPage"));
const CalendarPage = lazy(() => import("./pages/Calendar/CalendarPage"));

// Reports pages
const ReportsPage = lazy(() => import("./pages/Reports/ReportsPage"));
const PipelineReportPage = lazy(() =>
  import("./pages/Reports/PipelineReportPage")
);
const PipelineReportPageDetails = lazy(() =>
  import("./pages/Reports/PipelineReportPageDetails")
);
const TeamsPipelineReportPage = lazy(() =>
  import("./pages/Reports/TeamsPipelineReportPage")
);
const TeamsPipelineReportDetailsPage = lazy(() =>
  import("./pages/Reports/TeamsPipelineReportDetailsPage")
);
const LoseReportPage = lazy(() =>
  import("./pages/Reports/LoseReportPage")
);
const LoseReportDetailsPage = lazy(() =>
  import("./pages/Reports/LoseReportDetailsPage")
);
const TeamOverviewReport = lazy(() =>
  import("./pages/Reports/TeamOverviewReport")
);
const SalesVisitAnalyticsPage = lazy(() =>
  import("./pages/Reports/SalesVisitAnalyticsPage")
);
const TicketOverviewReport = lazy(() =>
  import("./pages/Reports/TicketOverviewReport")
);
const OmnichannelReportPage = lazy(() =>
  import("./pages/Reports/OmnichannelReportPage")
);

// Support pages
const TicketListPage = lazy(() => import("./pages/Support/TicketListPage"));
const TicketDetailPage = lazy(() => import("./pages/Support/TicketDetailPage"));
const WorkflowsPage = lazy(() => import("./pages/Settings/Workflows/WorkflowsPage"));
const WorkflowExecutionsPage = lazy(() => import("./pages/Settings/Workflows/WorkflowExecutionsPage"));

// Settings pages
const SettingsPage = lazy(() => import("./pages/Settings/SettingsPage"));
const NotificationSettings = lazy(() => import("./pages/Settings/Notifications/NotificationSettings"));
const ContactPositionsPage = lazy(() => import("./pages/Settings/ContactPositions/ContactPositionsPage"));
const ProvinceManagementPage = lazy(() => import("./pages/Settings/Provinces/ProvinceManagementPage"));
const LoseReasonsPage = lazy(() => import("./pages/Settings/LoseReasons/LoseReasonsPage"));
const SalesTargetsPage = lazy(() => import("./pages/Settings/SalesTargets/SalesTargetsPage"));
const CompanySettingsPage = lazy(() => import("./pages/Settings/Company/CompanySettingsPage"));
const OmnichannelProviderAppsPage = lazy(() => import("./pages/Settings/Omnichannel/OmnichannelProviderAppsPage"));
const EmailSettingsPage = lazy(() => import("./pages/Settings/Email/EmailSettingsPage"));
const MarketingEmailSettingsPage = lazy(() => import("./pages/Settings/Marketing/MarketingEmailSettingsPage"));
const VisitTopicsPage = lazy(() => import("./pages/Settings/VisitTopics/VisitTopicsPage"));
const TicketTopicSettingsPage = lazy(() => import("./pages/Settings/Ticket/TicketTopicSettingsPage"));
const TicketSystemsPage = lazy(() => import("./pages/Settings/Ticket/TicketSystemsPage"));
const TicketStatusSettingsPage = lazy(() => import("./pages/Settings/Ticket/TicketStatusSettingsPage"));
const TicketGeneralSettingsPage = lazy(() => import("./pages/Settings/Ticket/TicketGeneralSettingsPage"));
const TicketFeedbackSettingsPage = lazy(() => import("./pages/Settings/Ticket/TicketFeedbackSettingsPage"));

// NND (BRC) Settings pages
const NndSettingsLayout = lazy(() => import("./NND/pages/NndSettingsLayout"));
const NndPowerMapPage = lazy(() => import("./NND/pages/admin/NndPowerMapPage"));
const NndMsoTypesPage = lazy(() => import("./NND/pages/admin/NndMsoTypesPage"));
const NndMsoGroupsPage = lazy(() => import("./NND/pages/admin/NndMsoGroupsPage"));
const NndPaintSystemsPage = lazy(() => import("./NND/pages/admin/NndPaintSystemsPage"));
const NndSegmentsPage = lazy(() => import("./NND/pages/admin/NndSegmentsPage"));
const NndInsurersPage = lazy(() => import("./NND/pages/admin/NndInsurersPage"));
const NndPaintBrandsPage = lazy(() => import("./NND/pages/admin/NndPaintBrandsPage"));
const NndProductTypesPage = lazy(() => import("./NND/pages/admin/NndProductTypesPage"));
const NndPainPointsPage = lazy(() => import("./NND/pages/admin/NndPainPointsPage"));
const NndVasItemsPage = lazy(() => import("./NND/pages/admin/NndVasItemsPage"));

// NND (BRC) Transaction pages
const NNDDataEntryPage = lazy(() => import("./NND/pages/NNDDataEntryPage"));
const NNDMsoDirectoryPage = lazy(() => import("./NND/pages/NNDMsoDirectoryPage"));
const NNDMsoDashboardPage = lazy(() => import("./NND/pages/NNDMsoDashboardPage"));
const EmailQueueLogPage = lazy(() => import("./pages/Settings/EmailQueueLogPage"));
const OmnichannelSettingsLayout = lazy(() => import("./pages/Omnichannel/OmnichannelSettingsLayout"));
const OmiAccountsPage = lazy(() => import("./pages/Settings/Omnichannel/OmiAccountsPage"));
const OmiKnowledgePage = lazy(() => import("./pages/Settings/Omnichannel/OmiKnowledgePage"));
const TestAiPage = lazy(() => import("./pages/Settings/Omnichannel/TestAiPage"));
const AssetGeneralSettingsPage = lazy(() => import("./pages/Settings/Asset/AssetGeneralSettingsPage"));
const AssetBrandSettingsPage = lazy(() => import("./pages/Settings/Asset/AssetBrandSettingsPage"));
const AssetCategorySettingsPage = lazy(() => import("./pages/Settings/Asset/AssetCategorySettingsPage"));
const ApiKeysSettingsPage = lazy(() => import("./pages/Settings/ApiKeys/ApiKeysSettingsPage"));
const OmiChannelsConfigPage = lazy(() => import("./pages/Settings/Omnichannel/OmiChannelsConfigPage"));
const GlobalAiConfigPage = lazy(() => import("./pages/Settings/AiConfig/GlobalAiConfigPage"));
const CalendarSyncLogPage = lazy(() => import("./pages/Settings/CalendarSyncLogPage"));

// External Import pages
const ExternalImportConfigList = lazy(() => import("./pages/Settings/ExternalImport/ExternalImportConfigList"));
const ExternalImportConfigForm = lazy(() => import("./pages/Settings/ExternalImport/ExternalImportConfigForm"));
const ExternalImportPage = lazy(() => import("./pages/Settings/ExternalImport/ExternalImportPage"));
const ExternalImportLogs = lazy(() => import("./pages/Settings/ExternalImport/ExternalImportLogs"));

// Data Integration pages
const DataIntegrationSection = lazy(() => import("./pages/Settings/ExternalImport/DataIntegrationSection"));


// Dashboard pages
const SalesTargetOverview = lazy(() => import("./pages/Dashboard/PerformanceDashboard/PerformanceDashboard"));
const UserPerformancePage = lazy(() => import("./pages/UserPerformance/UserPerformancePage"));
const EmployeePerformanceHubPage = lazy(() => import("./pages/Reports/EmployeePerformanceHubPage"));

// Admin pages
const CustomFieldsPage = lazy(() => import("./pages/Settings/CustomFields/CustomFieldsPage"));
const ActivityLogsPage = lazy(() => import("./pages/Settings/ActivityLogs/ActivityLogsPage"));
const UsersPage = lazy(() => import("./pages/UserManagement/UsersPage"));
const RolesPage = lazy(() => import("./pages/UserManagement/RolesPage"));
const MenuManagementPage = lazy(() => import("./pages/Settings/Menus/MenuManagementPage"));
const PermissionMatrixPage = lazy(() => import("./pages/UserManagement/PermissionMatrixPage"));
const AddUserPage = lazy(() => import("./pages/UserManagement/AddUserPage"));
const EditUserPage = lazy(() => import("./pages/UserManagement/EditUserPage"));
const StageManagementPage = lazy(() => import("./pages/Settings/StageManagement/StageManagementPage"));
const ProductManagement = lazy(() => import("./pages/Settings/Products/ProductManagement"));
const ImportProductPage = lazy(() => import("./pages/Settings/Products/ImportProductPage"));
const LicensePage = lazy(() => import("./pages/Settings/License/LicensePage"));
const FileManagerPage = lazy(() => import("./pages/Settings/FileManager/FileManagerPage"));

const CustomerTagsPage = lazy(() => import("./pages/Settings/CustomerTags/CustomerTagsPage"));
const OpportunityTagsPage = lazy(() => import("./pages/Settings/OpportunityTags/OpportunityTagsPage"));

// Member & Loyalty Settings
const MemberGeneralSettingsPage = lazy(() => import("./pages/Settings/Member/MemberGeneralSettingsPage"));
const MemberAuthSettingsPage = lazy(() => import("./pages/Settings/Member/MemberAuthSettingsPage"));
const MemberSmsLogsPage = lazy(() => import("./pages/Settings/Member/MemberSmsLogsPage"));
const MemberTiersPage = lazy(() => import("./pages/Settings/Member/MemberTiersPage"));
const MemberPointsRulesPage = lazy(() => import("./pages/Settings/Member/MemberPointsRulesPage"));
const MemberRewardsPage = lazy(() => import("./pages/Member/MemberRewardsPage"));
const MemberRewardReportPage = lazy(() => import("./pages/Member/MemberRewardReportPage"));
const MemberApiIntegrationsPage = lazy(() => import("./pages/Settings/Member/MemberApiIntegrationsPage"));
const MemberOutboundSyncPage = lazy(() => import("./pages/Settings/Member/MemberOutboundSyncPage"));
const MemberImportExcelPage = lazy(() => import("./pages/Settings/Member/MemberImportExcelPage"));
const MemberPDPASettingsPage = lazy(() => import("./pages/Settings/Member/MemberPDPASettingsPage"));

// Member pages
const MemberListPage = lazy(() => import("./pages/Member/MemberListPage"));
const MemberSettingsLayout = lazy(() => import("./pages/Member/MemberSettingsLayout"));
const MemberImportMigrationPage = lazy(() => import("./pages/Settings/Member/MemberImportMigrationPage"));
const MemberDetailPage = lazy(() => import("./pages/Member/MemberDetailPage"));
const MemberRedemptionVerifyPage = lazy(() => import("./pages/Member/MemberRedemptionVerifyPage"));
const MemberBroadcastPage = lazy(() => import("./pages/Member/MemberBroadcastPage"));
const MemberLineFollowersPage = lazy(() => import("./pages/Member/MemberLineFollowersPage"));
const MemberVouchersPage = lazy(() => import("./pages/Member/MemberVouchersPage"));

// Teams pages
const TeamsPage = lazy(() => import("./pages/Teams/TeamsPage"));

// Auth pages
const LoginPage = lazy(() => import("./pages/Login/LoginPage"));
const LogoutPage = lazy(() => import("./pages/Logout/LogoutPage"));
const ProfilePage = lazy(() => import("./pages/Profile/ProfilePage"));

// Notifications
const NotificationsPage = lazy(() => import("./pages/Notifications/NotificationsPage"));

// CDP pages
const CdpProfilesPage = lazy(() => import("./pages/CDP/CdpProfilesPage"));
const CdpProfileDetailPage = lazy(() => import("./pages/CDP/CdpProfileDetailPage"));
const CdpTransactionsPage = lazy(() => import("./pages/CDP/CdpTransactionsPage"));
const CdpDataSourcesPage = lazy(() => import("./pages/CDP/CdpDataSourcesPage"));
const CdpImportPage = lazy(() => import("./pages/CDP/CdpImportPage"));
const CdpReviewQueuePage = lazy(() => import("./pages/CDP/CdpReviewQueuePage"));
const CdpSegmentsPage = lazy(() => import("./pages/CDP/CdpSegmentsPage"));
const CdpSegmentDetailPage = lazy(() => import("./pages/CDP/CdpSegmentDetailPage"));
const CdpScoringRulesPage = lazy(() => import("./pages/CDP/CdpScoringRulesPage"));
const CampaignListPage = lazy(() => import("./pages/CDP/CampaignListPage"));
const CampaignFormPage = lazy(() => import("./pages/CDP/CampaignFormPage"));
const CampaignDetailPage = lazy(() => import("./pages/CDP/CampaignDetailPage"));
const JourneyListPage = lazy(() => import("./pages/CDP/JourneyListPage"));
const JourneyBuilderPage = lazy(() => import("./pages/CDP/JourneyBuilderPage"));

// Sales Visit pages
const VisitListPage = lazy(() => import("./pages/SalesVisit/VisitListPage"));
const VisitPlanPage = lazy(() => import("./pages/SalesVisit/VisitPlanPage"));
const VisitReportPage = lazy(() => import("./pages/SalesVisit/VisitReportPage"));
const VisitDetailPage = lazy(() => import("./pages/SalesVisit/VisitDetailPage"));
const VisitEditPage = lazy(() => import("./pages/SalesVisit/VisitEditPage"));

const NotFoundPage = lazy(() => import("./pages/NotFound/NotFoundPage"));
const PublicTicketPortal = lazy(() => import("./pages/Public/PublicTicketPortal"));
const PublicAssetLookup = lazy(() => import("./pages/Public/PublicAssetLookup"));
const SupportFeedbackPage = lazy(() => import("./pages/Public/SupportFeedbackPage"));
const ChangelogPage = lazy(() => import("./pages/Changelog/ChangelogPage"));

const OmnichannelChat = lazy(() => import("./pages/Omnichannel/OmnichannelChat"));

function App() {
  document.title = `CRM System - ${process.env.REACT_APP_COMPANY_NAME || "Your Company"
    }`;

  return (
    <AuthProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Router>
        <Suspense
          fallback={
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "60vh" }}
            >
              <Spinner animation="border" />
            </div>
          }
        >
          <NotificationProvider>
            <TicketStatusProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/public/ticket" element={<PublicTicketPortal />} />
                <Route path="/public/asset/:tag" element={<PublicAssetLookup />} />
                <Route path="/public/feedback" element={<SupportFeedbackPage />} />

                <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                  <Route index element={<DashboardPage />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="sales-targets" element={<SalesTargetOverview />} />
                  <Route path="/customers" element={<CustomerListPage />} />
                  <Route path="/customers/add" element={<AddCustomerPage />} />
                  <Route path="/customers/import" element={<ImportCustomerPage />} />
                  <Route path="/customers/:id/edit" element={<EditCustomerPage />} />
                  <Route path="/customers/:id" element={<CustomerDetailPage />} />
                  {process.env.REACT_APP_ENABLE_NND === 'true' ? (
                    <>
                      <Route path="/customers/:id/nnd-entry" element={<NNDDataEntryPage />} />
                      <Route path="/nnd/mso-directory" element={<NNDMsoDirectoryPage />} />
                      <Route path="/nnd/mso-dashboard" element={<NNDMsoDashboardPage />} />
                      <Route path="/nnd/mso-dashboard/:groupId" element={<NNDMsoDashboardPage />} />

                      {/* NND Settings Hub */}
                      <Route path="/nnd/settings" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndSettingsLayout /></ProtectedRoute>}>
                        <Route index element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndMsoGroupsPage /></ProtectedRoute>} />
                        <Route path="mso-groups" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndMsoGroupsPage /></ProtectedRoute>} />
                        <Route path="mso-types" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndMsoTypesPage /></ProtectedRoute>} />
                        <Route path="segments" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndSegmentsPage /></ProtectedRoute>} />
                        <Route path="power-map" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPowerMapPage /></ProtectedRoute>} />
                        <Route path="paint-systems" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPaintSystemsPage /></ProtectedRoute>} />
                        <Route path="paint-brands" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPaintBrandsPage /></ProtectedRoute>} />
                        <Route path="product-types" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndProductTypesPage /></ProtectedRoute>} />
                        <Route path="insurers" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndInsurersPage /></ProtectedRoute>} />
                        <Route path="pain-points" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPainPointsPage /></ProtectedRoute>} />
                        <Route path="vas-items" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndVasItemsPage /></ProtectedRoute>} />
                      </Route>
                    </>
                  ) : null}
                  <Route path="/opportunities" element={<OpportunitiesPage />} />
                  <Route path="/quotes" element={<QuotesPage />} />
                  <Route path="/quotes/create" element={<QuoteFormPage />} />
                  <Route path="/quotes/:id/edit" element={<QuoteFormPage />} />
                  <Route path="/opportunities/import" element={<ImportOpportunityPage />} />
                  <Route path="/opportunities/:id" element={<OpportunityDetailPage />} />
                  {/* <Route path="/opportunities/:id/v2" element={<OpportunityDetailPageV2 />} /> */}
                  <Route path="/tasks" element={<TasksPage />} />
                  <Route path="/assets" element={<AssetsPage />} />
                  <Route path="/assets/import" element={<ImportAssetPage />} />
                  <Route path="/assets/:id" element={<AssetDetailPage />} />
                  <Route path="/calendar" element={<CalendarPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/reports/pipeline" element={<PipelineReportPage />} />
                  <Route path="/reports/pipeline-details" element={<PipelineReportPageDetails />} />
                  <Route path="/reports/teams-pipeline" element={<TeamsPipelineReportPage />} />
                  <Route path="/reports/teams-pipeline-details" element={<TeamsPipelineReportDetailsPage />} />
                  <Route path="/reports/lose" element={<LoseReportPage />} />
                  <Route path="/reports/lose-details" element={<LoseReportDetailsPage />} />
                  <Route path="/reports/team-overview" element={<TeamOverviewReport />} />
                  <Route path="/reports/sales-visits" element={<SalesVisitAnalyticsPage />} />
                  <Route path="/reports/tickets/overview" element={<TicketOverviewReport />} />
                  <Route path="/reports/omnichannel" element={<OmnichannelReportPage />} />
                  <Route path="/reports/employee-performance" element={<EmployeePerformanceHubPage />} />
                  <Route path="/users/:userId/performance" element={<UserPerformancePage />} />
                  <Route path="/my-performance" element={<UserPerformancePage />} />


                  {/* Settings Group with Shared Layout */}
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route index element={<SettingsPage />} />
                    <Route path="workflows" element={<WorkflowsPage />} />
                    <Route path="workflow-executions" element={<WorkflowExecutionsPage />} />
                    <Route path="custom-fields" element={<ProtectedRoute allowedRoles={['admin']}><CustomFieldsPage /></ProtectedRoute>} />
                    <Route path="provinces" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ProvinceManagementPage /></ProtectedRoute>} />
                    <Route path="customer-tags" element={<ProtectedRoute allowedRoles={['admin']}><CustomerTagsPage /></ProtectedRoute>} />
                    <Route path="opportunity-tags" element={<ProtectedRoute allowedRoles={['admin']}><OpportunityTagsPage /></ProtectedRoute>} />

                    {/* Member & Loyalty Settings */}
                    <Route path="member-migration" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberImportMigrationPage /></ProtectedRoute>} />
                    <Route path="member/general" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberGeneralSettingsPage /></ProtectedRoute>} />
                    <Route path="member/pdpa" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberPDPASettingsPage /></ProtectedRoute>} />
                    <Route path="member/auth" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberAuthSettingsPage /></ProtectedRoute>} />
                    <Route path="member/sms-logs" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberSmsLogsPage /></ProtectedRoute>} />
                    <Route path="member/tiers" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberTiersPage /></ProtectedRoute>} />
                    <Route path="member/points-rules" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberPointsRulesPage /></ProtectedRoute>} />
                    <Route path="member/api-integrations" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberApiIntegrationsPage /></ProtectedRoute>} />
                    <Route path="member/outbound-sync" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberOutboundSyncPage /></ProtectedRoute>} />
                    <Route path="member/import-excel" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberImportExcelPage /></ProtectedRoute>} />
                    <Route path="contact-positions" element={<ProtectedRoute allowedRoles={['admin']}><ContactPositionsPage /></ProtectedRoute>} />
                    <Route path="lose-reasons" element={<ProtectedRoute allowedRoles={['admin']}><LoseReasonsPage /></ProtectedRoute>} />
                    <Route path="sales-targets" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><SalesTargetsPage /></ProtectedRoute>} />
                    <Route path="company" element={<ProtectedRoute allowedRoles={['admin']}><CompanySettingsPage /></ProtectedRoute>} />
                    <Route path="omni-provider-apps" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmnichannelProviderAppsPage /></ProtectedRoute>} />
                    <Route path="email-config" element={<ProtectedRoute allowedRoles={['admin']}><EmailSettingsPage /></ProtectedRoute>} />
                    <Route path="marketing-email" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><MarketingEmailSettingsPage /></ProtectedRoute>} />
                    <Route path="visit-topics" element={<ProtectedRoute allowedRoles={['admin']}><VisitTopicsPage /></ProtectedRoute>} />
                    <Route path="file-management" element={<ProtectedRoute allowedRoles={['admin']}><FileManagerPage /></ProtectedRoute>} />
                    <Route path="external-import" element={<ProtectedRoute allowedRoles={['admin']}><ExternalImportConfigList /></ProtectedRoute>} />
                    <Route path="external-import/new" element={<ProtectedRoute allowedRoles={['admin']}><ExternalImportConfigForm /></ProtectedRoute>} />
                    <Route path="external-import/edit/:id" element={<ProtectedRoute allowedRoles={['admin']}><ExternalImportConfigForm /></ProtectedRoute>} />
                    <Route path="external-import/import/:configId" element={<ProtectedRoute allowedRoles={['admin']}><ExternalImportPage /></ProtectedRoute>} />
                    <Route path="external-import/logs" element={<ProtectedRoute allowedRoles={['admin']}><ExternalImportLogs /></ProtectedRoute>} />
                    <Route path="external-import/data-integration" element={<ProtectedRoute allowedRoles={['admin']}><DataIntegrationSection /></ProtectedRoute>} />
                    <Route path="ticket-general" element={<ProtectedRoute allowedRoles={['admin']}><TicketGeneralSettingsPage /></ProtectedRoute>} />
                    <Route path="ticket-topics" element={<ProtectedRoute allowedRoles={['admin']}><TicketTopicSettingsPage /></ProtectedRoute>} />
                    <Route path="ticket-systems" element={<ProtectedRoute allowedRoles={['admin']}><TicketSystemsPage /></ProtectedRoute>} />
                    <Route path="ticket-Status" element={<ProtectedRoute allowedRoles={['admin']}><TicketStatusSettingsPage /></ProtectedRoute>} />
                    <Route path="ticket-feedback" element={<ProtectedRoute allowedRoles={['admin']}><TicketFeedbackSettingsPage /></ProtectedRoute>} />
                    <Route path="menus" element={<ProtectedRoute allowedRoles={['admin']}><MenuManagementPage /></ProtectedRoute>} />
                    <Route path="permissions" element={<ProtectedRoute allowedRoles={['admin']}><PermissionMatrixPage /></ProtectedRoute>} />
                    <Route path="stages" element={<ProtectedRoute allowedRoles={['admin']}><StageManagementPage /></ProtectedRoute>} />
                    <Route path="products" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ProductManagement /></ProtectedRoute>} />
                    <Route path="products/import" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ImportProductPage /></ProtectedRoute>} />
                    <Route path="license" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><LicensePage /></ProtectedRoute>} />
                    <Route path="/settings/activity-logs" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ActivityLogsPage /></ProtectedRoute>} />
                    <Route path="email-queue" element={<ProtectedRoute allowedRoles={['admin']}><EmailQueueLogPage /></ProtectedRoute>} />
                    <Route path="calendar-sync-logs" element={<ProtectedRoute allowedRoles={['admin']}><CalendarSyncLogPage /></ProtectedRoute>} />
                    <Route path="omni-accounts" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiAccountsPage /></ProtectedRoute>} />
                    <Route path="omni-knowledge" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiKnowledgePage /></ProtectedRoute>} />
                    <Route path="test-ai" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><TestAiPage /></ProtectedRoute>} />
                    <Route path="omi-channels" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiChannelsConfigPage /></ProtectedRoute>} />
                    <Route path="ai-config" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><GlobalAiConfigPage /></ProtectedRoute>} />
                    <Route path="assets" element={<ProtectedRoute allowedRoles={['admin']}><AssetGeneralSettingsPage /></ProtectedRoute>} />
                    <Route path="asset-brands" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AssetBrandSettingsPage /></ProtectedRoute>} />
                    <Route path="asset-categories" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AssetCategorySettingsPage /></ProtectedRoute>} />
                    <Route path="api-keys" element={<ProtectedRoute allowedRoles={['admin']}><ApiKeysSettingsPage /></ProtectedRoute>} />

                    <Route path="users" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><UsersPage /></ProtectedRoute>} />
                    <Route path="roles" element={<ProtectedRoute allowedRoles={['admin']}><RolesPage /></ProtectedRoute>} />
                    <Route path="teams" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><TeamsPage /></ProtectedRoute>} />
                    {process.env.REACT_APP_ENABLE_NND === 'true' ? (
                      <>
                        <Route path="nnd" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPowerMapPage /></ProtectedRoute>} />
                        <Route path="nnd/power-map" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPowerMapPage /></ProtectedRoute>} />
                        <Route path="nnd/mso-types" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndMsoTypesPage /></ProtectedRoute>} />
                        <Route path="nnd/mso-groups" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndMsoGroupsPage /></ProtectedRoute>} />
                        <Route path="nnd/paint-systems" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPaintSystemsPage /></ProtectedRoute>} />
                        <Route path="nnd/segments" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndSegmentsPage /></ProtectedRoute>} />
                        <Route path="nnd/insurers" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndInsurersPage /></ProtectedRoute>} />
                        <Route path="nnd/paint-brands" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPaintBrandsPage /></ProtectedRoute>} />
                        <Route path="nnd/product-types" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndProductTypesPage /></ProtectedRoute>} />
                        <Route path="nnd/pain-points" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndPainPointsPage /></ProtectedRoute>} />
                        <Route path="nnd/vas-items" element={<ProtectedRoute permissionKey="nnd-settings" allowedRoles={['admin']}><NndVasItemsPage /></ProtectedRoute>} />
                      </>
                    ) : null}
                    <Route path="users/add" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><AddUserPage /></ProtectedRoute>} />
                    <Route path="users/edit/:id" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><EditUserPage /></ProtectedRoute>} />
                    <Route path="changelog" element={<ProtectedRoute allowedRoles={['admin']}><ChangelogPage /></ProtectedRoute>} />
                  </Route>


                  {/* CDP Routes */}
                  <Route path="/cdp/profiles" element={<CdpProfilesPage />} />
                  <Route path="/cdp/profiles/:id" element={<CdpProfileDetailPage />} />
                  <Route path="/cdp/transactions" element={<CdpTransactionsPage />} />
                  <Route path="/cdp/data-sources" element={<CdpDataSourcesPage />} />
                  <Route path="/cdp/import" element={<CdpImportPage />} />
                  <Route path="/cdp/review-queue" element={<CdpReviewQueuePage />} />
                  <Route path="/cdp/segments" element={<CdpSegmentsPage />} />
                  <Route path="/cdp/segments/:id" element={<CdpSegmentDetailPage />} />
                  <Route path="/cdp/scoring-rules" element={<CdpScoringRulesPage />} />
                  <Route path="/cdp/campaigns" element={<CampaignListPage />} />
                  <Route path="/cdp/journeys" element={<JourneyListPage />} />
                  <Route path="/cdp/journeys/builder/:id" element={<JourneyBuilderPage />} />
                  <Route path="/cdp/campaigns/add" element={<CampaignFormPage />} />
                  <Route path="/cdp/campaigns/edit/:id" element={<CampaignFormPage />} />
                  <Route path="/cdp/campaigns/:id" element={<CampaignDetailPage />} />

                  {/* Member & Loyalty */}
                  <Route path="/members" element={<MemberListPage />} />
                  <Route path="/line-followers" element={<MemberLineFollowersPage />} />
                  <Route path="/broadcast" element={<MemberBroadcastPage />} />
                  <Route path="/members/:id" element={<MemberDetailPage />} />
                  <Route path="/verify-voucher" element={<MemberRedemptionVerifyPage />} />
                  <Route path="/vouchers" element={<MemberVouchersPage />} />
                  <Route path="/member/vouchers" element={<MemberVouchersPage />} />
                  <Route path="/rewards" element={<MemberRewardsPage />} />
                  <Route path="/rewards/:id/report" element={<MemberRewardReportPage />} />

                  {/* Member & Loyalty Settings Hub */}
                  <Route path="/member/settings" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberSettingsLayout /></ProtectedRoute>}>
                    <Route index element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberGeneralSettingsPage /></ProtectedRoute>} />
                    <Route path="general" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberGeneralSettingsPage /></ProtectedRoute>} />
                    <Route path="tiers" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberTiersPage /></ProtectedRoute>} />
                    <Route path="points-rules" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberPointsRulesPage /></ProtectedRoute>} />
                    <Route path="auth" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberAuthSettingsPage /></ProtectedRoute>} />
                    <Route path="pdpa" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberPDPASettingsPage /></ProtectedRoute>} />
                    <Route path="sms-logs" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberSmsLogsPage /></ProtectedRoute>} />
                    <Route path="api-integrations" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberApiIntegrationsPage /></ProtectedRoute>} />
                    <Route path="outbound-sync" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberOutboundSyncPage /></ProtectedRoute>} />
                    <Route path="import-excel" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin', 'manager']}><MemberImportExcelPage /></ProtectedRoute>} />
                    <Route path="migration" element={<ProtectedRoute permissionKey="member-settings" allowedRoles={['admin']}><MemberImportMigrationPage /></ProtectedRoute>} />
                  </Route>

                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/sales-visits" element={<VisitListPage />} />
                  <Route path="/sales-visits/create" element={<VisitPlanPage />} />
                  <Route path="/sales-visits/:id/report" element={<VisitReportPage />} />
                  <Route path="/sales-visits/:id/edit" element={<VisitEditPage />} />
                  <Route path="/sales-visits/:id" element={<VisitDetailPage />} />
                  <Route path="/tickets" element={<TicketListPage />} />
                                    <Route path="/tickets/:id" element={<TicketDetailPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/notification-settings" element={<NotificationSettings />} />

                  <Route path="/logout" element={<LogoutPage />} />
                  <Route path="/omnichannel" element={<OmnichannelChat />} />

                  {/* Chat Hub & Omnichannel Settings Hub */}
                  <Route path="/omnichannel/settings" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmnichannelSettingsLayout /></ProtectedRoute>}>
                    <Route index element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiAccountsPage /></ProtectedRoute>} />
                    <Route path="accounts" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiAccountsPage /></ProtectedRoute>} />
                    <Route path="providers" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmnichannelProviderAppsPage /></ProtectedRoute>} />
                    <Route path="channels" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiChannelsConfigPage /></ProtectedRoute>} />
                    <Route path="knowledge" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><OmiKnowledgePage /></ProtectedRoute>} />
                    <Route path="test-ai" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><TestAiPage /></ProtectedRoute>} />
                    <Route path="ai-config" element={<ProtectedRoute permissionKey="omnichannel-settings" allowedRoles={['admin']}><GlobalAiConfigPage /></ProtectedRoute>} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                <Route path="*" element={<NotFoundPage standalone={true} />} />
              </Routes>
            </TicketStatusProvider>
          </NotificationProvider>
        </Suspense>
        <PWAInstallPrompt />
      </Router>
    </AuthProvider>
  );
}

export default App;
